import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { getPublicEvents } from '@/lib/api';
import Marquee from 'react-fast-marquee';
import AuthModal from '@/components/AuthModal';
import WaitlistModal from '@/components/WaitlistModal';
import BunchhLogo from '@/components/BunchhLogo';
import { motion } from 'framer-motion';
import { Menu, X, Lock } from 'lucide-react';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } } };
const stagger = { visible: { transition: { staggerChildren: 0.15 } } };

const PHOTO_STRIP = [
  { url: 'https://images.unsplash.com/photo-1753806389159-92b9c6f7774a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxmcmllbmRzJTIwZ2F0aGVyaW5nJTIwZGlubmVyJTIwSW5kaWElMjBNdW1iYWl8ZW58MHx8fHwxNzc1NTYxNTIyfDA&ixlib=rb-4.1.0&q=85', label: 'Marine Drive · Mumbai' },
  { url: 'https://images.unsplash.com/photo-1673558827213-4e20ca2a1787?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxmcmllbmRzJTIwY2FmZSUyMEluZGlhJTIwY29sb3JmdWwlMjBzb2NpYWwlMjBnYXRoZXJpbmd8ZW58MHx8fHwxNzc1NTYxNTM2fDA&ixlib=rb-4.1.0&q=85', label: 'Cafe night · Mumbai' },
  { url: 'https://images.unsplash.com/photo-1742677259590-69bc3670c9f3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxmcmllbmRzJTIwY2FmZSUyMEluZGlhJTIwY29sb3JmdWwlMjBzb2NpYWwlMjBnYXRoZXJpbmd8ZW58MHx8fHwxNzc1NTYxNTM2fDA&ixlib=rb-4.1.0&q=85', label: 'Brunch hangout · Mumbai' },
  { url: 'https://images.unsplash.com/photo-1588676247761-be44a0265858?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxmcmllbmRzJTIwY2FmZSUyMEluZGlhJTIwY29sb3JmdWwlMjBzb2NpYWwlMjBnYXRoZXJpbmd8ZW58MHx8fHwxNzc1NTYxNTM2fDA&ixlib=rb-4.1.0&q=85', label: 'Evening out · Mumbai' },
  { url: 'https://images.unsplash.com/photo-1669740621712-d36dd3835c65?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxmcmllbmRzJTIwZ2F0aGVyaW5nJTIwZGlubmVyJTIwSW5kaWElMjBNdW1iYWl8ZW58MHx8fHwxNzc1NTYxNTIyfDA&ixlib=rb-4.1.0&q=85', label: 'Bandstand walks · Mumbai' },
  { url: 'https://images.unsplash.com/photo-1740765937771-c74855bfbb3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxmcmllbmRzJTIwY2FmZSUyMEluZGlhJTIwY29sb3JmdWwlMjBzb2NpYWwlMjBnYXRoZXJpbmd8ZW58MHx8fHwxNzc1NTYxNTM2fDA&ixlib=rb-4.1.0&q=85', label: 'Festival vibes · Mumbai' },
];

const EVENT_TYPE_GRADIENTS = {
  'Art Evening':  'linear-gradient(135deg,#3D1A08 0%,#6B2810 100%)',
  'Game Night':   'linear-gradient(135deg,#1F1A2A 0%,#2E2040 100%)',
  'Brunch':       'linear-gradient(135deg,#2A1F0A 0%,#3D2E10 100%)',
  'Film Night':   'linear-gradient(135deg,#201A20 0%,#3A2A38 100%)',
  'Hike':         'linear-gradient(135deg,#0D2A1A 0%,#1A4A2E 100%)',
  'Book Circle':  'linear-gradient(135deg,#1A1A2E 0%,#16213E 100%)',
  'Dinner':       'linear-gradient(135deg,#2A0A0A 0%,#4A1010 100%)',
  'default':      'linear-gradient(135deg,#1a1a2e 0%,#2d2d44 100%)',
};

const FAQS = [
  { q: 'Is this a dating app?', a: "No. We plan activities and bring people together. People who come to Bunchh are looking for genuine company, interesting conversation, and plans worth leaving home for." },
  { q: 'Will it be awkward?', a: 'Probably for the first few minutes — that\'s just being human. But we design plans specifically to get past that quickly. The activity does the work.' },
  { q: 'How does the approval process work?', a: 'You fill in your details and we review every application personally. We\'re looking for people who are genuine and open. We build the room so it actually works.' },
  { q: 'How much does it cost?', a: 'Plans are priced individually — usually between \u20B9599 and \u20B93,499 depending on the activity. No membership fee. Book what you want, skip what you don\'t.' },
  { q: 'What happens after I sign up?', a: "You'll land on our waitlist. When we have capacity in your city, you'll get an email with your access code. Use it to log in and book." },
];

const TESTIMONIALS = [
  { quote: '"I stopped saying \'I wish I had plans\' and started actually making them."', name: 'Anonymous, 29', city: 'Mumbai', avatar: 'A', bg: '#F2C4B0', color: '#9d341d' },
  { quote: '"All my friends are away. This helped me build my weekends."', name: 'Aman, 25', city: 'Mumbai', avatar: 'A', bg: '#FBDFA0', color: '#7A5A10' },
  { quote: '"I almost didn\'t go. Then I ended up staying two hours past the plan ended, still talking."', name: 'Priya, 35', city: 'Mumbai', avatar: 'P', bg: '#D4EBCC', color: '#2A5218' },
];

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [liveEvents, setLiveEvents] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    getPublicEvents()
      .then(res => setLiveEvents(res.data))
      .catch(() => setLiveEvents([]));
  }, []);

  const scrollToId = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleMemberPortal = () => {
    if (user) {
      if (user.role === 'admin') navigate('/admin');
      else navigate('/members');
    } else {
      setShowAuth(true);
    }
  };

  return (
    <div data-testid="landing-page">
      {/* NAV */}
      <nav className={`bunchh-nav ${scrolled ? 'scrolled' : ''}`} data-testid="main-nav">
        <div className="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <BunchhLogo height={32} />
        </div>
        <div className="nav-links">
          <span className="nav-link" onClick={() => scrollToId('how')}>How it works</span>
          <span className="nav-link" onClick={() => scrollToId('faq')}>FAQ</span>
        </div>
        <div className="nav-btns">
          <button className="btn-ghost btn-sm" onClick={() => setShowAuth(true)} data-testid="nav-login-btn">Login</button>
          <button className="btn-coral btn-sm" onClick={() => setShowWaitlist(true)} data-testid="nav-waitlist-btn">Join waitlist</button>
          <button
            className="btn-ghost btn-sm"
            onClick={() => setMobileMenu(!mobileMenu)}
            style={{ display: 'none', padding: 8 }}
            data-testid="mobile-menu-btn"
            id="mobile-menu-toggle"
          >
            {mobileMenu ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenu && (
        <div style={{
          position: 'fixed', top: 56, left: 0, right: 0, zIndex: 99,
          background: 'var(--card-bg)', borderBottom: '1px solid var(--border)',
          padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12
        }} data-testid="mobile-menu">
          <span className="nav-link" onClick={() => { scrollToId('how'); setMobileMenu(false); }}>How it works</span>
          <span className="nav-link" onClick={() => { scrollToId('faq'); setMobileMenu(false); }}>FAQ</span>
          <button className="btn-coral btn-sm" onClick={() => { setShowWaitlist(true); setMobileMenu(false); }} style={{ marginTop: 4 }}>Join waitlist</button>
        </div>
      )}

      {/* HERO */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-blob hero-blob-1" />
        <div className="hero-blob hero-blob-2" />
        <motion.div className="hero-content" initial="hidden" animate="visible" variants={stagger}>
          <motion.div className="hero-eyebrow" variants={fadeUp}>Something is happening in Mumbai</motion.div>
          <motion.h1 className="hero-headline" variants={fadeUp}>
            You've been<br /><em>looking for this.</em>
          </motion.h1>
          <motion.p className="hero-sub" variants={fadeUp}>
            We find the spot. Build the plan. Bring the people. You show up.
          </motion.p>
          <motion.div className="hero-cta" variants={fadeUp}>
            <button className="btn-coral" onClick={() => setShowWaitlist(true)} data-testid="hero-waitlist-btn">Request access</button>
            <button className="btn-ghost" onClick={() => scrollToId('how')} data-testid="hero-how-btn">See how it works</button>
          </motion.div>
        </motion.div>
      </section>

      {/* TICKER */}
      <div className="ticker-section">
        <Marquee speed={30} gradient={false} pauseOnHover>
          {['Brunches', 'Art Evenings', 'Hikes', 'Game Nights', 'Film Nights', 'Book Circles', 'Weekend Plans', 'New Cities'].map((t, i) => (
            <span className="ticker-item" key={i}>{t}<span className="ticker-dot" /></span>
          ))}
        </Marquee>
      </div>

      {/* UPCOMING EVENTS */}
      {liveEvents.length > 0 && (
        <section className="section" data-testid="events-section" style={{ background: 'var(--cream)' }}>
          <motion.div className="section-inner" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={stagger}>
            <motion.div className="eyebrow" variants={fadeUp}>What's on</motion.div>
            <motion.h2 className="section-h2" variants={fadeUp}>Upcoming plans.</motion.h2>
            <motion.p className="section-sub" variants={fadeUp}>Join the waitlist to book your spot.</motion.p>
            <motion.div className="members-grid" style={{ padding: '32px 0 0', maxWidth: '100%' }} variants={stagger}>
              {liveEvents.map((event, idx) => (
                <motion.div className="event-card" key={event.id} variants={fadeUp}>
                  <div
                    className="event-card-img"
                    style={{
                      backgroundImage: event.image_url ? `url(${event.image_url})` : 'none',
                      background: event.image_url ? undefined : (EVENT_TYPE_GRADIENTS[event.event_type] || EVENT_TYPE_GRADIENTS.default)
                    }}
                  >
                    <div className="event-card-badges">
                      <span className="event-badge">{event.event_type}</span>
                      <span className="event-badge">{event.city}</span>
                    </div>
                  </div>
                  <div className="event-card-body">
                    <div className="event-card-title">{event.title}</div>
                    {event.vibe && <div className="event-card-vibe">{event.vibe}</div>}
                    <div className="event-card-meta">
                      {event.date && <span>📅 {event.date}</span>}
                      {event.time && <span>🕐 {event.time}</span>}
                      {event.city && <span>📍 {event.city}</span>}
                    </div>
                    <div className="event-card-footer">
                      <div>
                        <div className="event-price">₹{event.price}</div>
                        <div className="event-spots" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {event.spots_left > 0 ? `${event.spots_left} spots left` : ''}
                        </div>
                      </div>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>
                        <Lock size={13} /> Members only
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </section>
      )}

      {/* PROBLEM */}
      <section className="problem-section" data-testid="problem-section">
        <motion.div className="problem-inner" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={stagger}>
          <motion.div className="problem-tag" variants={fadeUp}>You're not alone in this</motion.div>
          <motion.h2 className="problem-headline" variants={fadeUp}>
            Mumbai is full of people trying to make plans that never actually happen.
          </motion.h2>
          <motion.div className="problem-stats" variants={stagger}>
            {[
              { num: '60%', desc: 'of young professionals in Indian metros feel their social circle has shrunk since moving for work' },
              { num: '40%', desc: 'of group chats dedicated to planning an outing never result in a physical meetup' },
              { num: '1 in 2', desc: "urban residents under 35 want to try new activities but don't have a crew to go with" },
            ].map((s, i) => (
              <motion.div className="pstat-card" key={i} variants={fadeUp}>
                <div className="pstat-num">{s.num}</div>
                <div className="pstat-desc">{s.desc}</div>
              </motion.div>
            ))}
          </motion.div>
          <motion.p className="problem-closer" variants={fadeUp}><em>That's what we are here for<br />to connect you with your bunchh</em></motion.p>
        </motion.div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section" id="how" style={{ background: 'var(--cream)' }} data-testid="how-section">
        <motion.div className="section-inner" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={stagger}>
          <motion.div className="eyebrow" variants={fadeUp}>How it works</motion.div>
          <motion.h2 className="section-h2" variants={fadeUp}>It's simpler than you think.</motion.h2>
          <motion.p className="section-sub" variants={fadeUp}>easier than making plans with your friends</motion.p>
          <motion.div className="steps-row" variants={stagger}>
            {[
              { num: '01', icon: '\u2726', title: 'Tell us a little about you', desc: "Every response is read by a real human who actually gives a damn" },
              { num: '02', icon: '\u25C8', title: 'See what\'s happening', desc: 'Once you\'re in, choose a plan and claim your spot' },
              { num: '03', icon: '\u2192', title: 'Just turn up & have fun', desc: "We'll share final details before the plan" },
            ].map((s, i) => (
              <motion.div className="step-card" key={i} variants={fadeUp}>
                <div className="step-num-bg">{s.num}</div>
                <div className="step-icon">{s.icon}</div>
                <div className="step-title">{s.title}</div>
                <div className="step-desc">{s.desc}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>


      {/* PHOTO STRIP */}
      <div className="photo-strip-section" data-testid="photo-strip">
        <Marquee speed={25} gradient={false} pauseOnHover>
          {PHOTO_STRIP.map((p, i) => (
            <div className="photo-card" key={i} style={{ marginRight: 16 }}>
              <img src={p.url} alt={p.label} loading="lazy" />
              <span className="photo-label">{p.label}</span>
            </div>
          ))}
        </Marquee>
      </div>

      {/* PROOF */}
      <section className="section" data-testid="proof-section">
        <motion.div className="section-inner" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={stagger}>
          <motion.div className="eyebrow" style={{ marginBottom: 32 }} variants={fadeUp}>From the people</motion.div>
          <motion.div className="testimonials-grid" variants={stagger}>
            {TESTIMONIALS.map((t, i) => (
              <motion.div className="t-card" key={i} variants={fadeUp}>
                <div className="t-stars">{'★'.repeat(5)}</div>
                <div className="t-quote">{t.quote}</div>
                <div className="t-author">
                  <div className="t-avatar" style={{ background: t.bg, color: t.color }}>{t.avatar}</div>
                  <div><div className="t-name">{t.name}</div><div className="t-meta">{t.city}</div></div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* MEDIA */}
      <section className="media-section" data-testid="media-section">
        <motion.div className="media-inner" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={stagger}>
          <motion.div variants={fadeUp}>
            <div className="eyebrow">As seen in</div>
            <h2 className="section-h2">People are talking about us.</h2>
          </motion.div>
          <motion.div className="media-grid" variants={stagger}>
            <motion.div className="media-card" variants={fadeUp}>
              <div className="media-pub">
                <div className="media-pub-badge" style={{ background: '#E8001A' }}>HT</div>
                <div><div className="media-pub-name">Hindustan Times</div><div className="media-pub-date">2025</div></div>
              </div>
              <div className="media-quote">Youngsters across India are showing up solo to planned community dinners for new friendships.</div>
              <a href="https://www.hindustantimes.com/htcity/htcity-foodies/table-for-whom-dining-with-strangers-is-gen-z-s-latest-social-trend-101753362541798.html" className="media-link" target="_blank" rel="noopener noreferrer">Read the story →</a>
            </motion.div>
            <motion.div className="media-card" variants={fadeUp}>
              <div className="media-pub">
                <div className="media-pub-badge" style={{ background: '#1a1a1a' }}>MD</div>
                <div><div className="media-pub-name">Mid-Day</div><div className="media-pub-date">2025</div></div>
              </div>
              <div className="media-quote">These meets thrive on flexibility. The goal is to discover people, converse without pressure… and leave as friends.</div>
              <a href="https://www.mid-day.com/sunday-mid-day/article/how-these-meetups-are-making-more-indians-find-comfort-in-strangers-23597262" className="media-link" target="_blank" rel="noopener noreferrer">Read the story →</a>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="faq-section" id="faq" data-testid="faq-section">
        <div className="faq-inner">
          <div className="eyebrow">The honest answers</div>
          <h2 className="section-h2" style={{ marginBottom: 32 }}>Things you're probably wondering.</h2>
          {FAQS.map((f, i) => (
            <div className="faq-item" key={i}>
              <button className="faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)} data-testid={`faq-item-${i}`}>
                <span>{f.q}</span>
                <span className={`faq-toggle ${openFaq === i ? 'open' : ''}`}>+</span>
              </button>
              <div className={`faq-a ${openFaq === i ? 'open' : ''}`}>{f.a}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section" id="waitlist" data-testid="cta-section">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div className="eyebrow" variants={fadeUp}>Join the waitlist</motion.div>
          <motion.h2 className="cta-h2" variants={fadeUp}>The people are<br />already <em>here.</em></motion.h2>
          <motion.p className="cta-sub" variants={fadeUp}>We read every application personally. When there's a spot for you, you'll hear from us.</motion.p>
          <motion.div className="cta-btns" variants={fadeUp}>
            <button className="btn-amber" onClick={() => setShowWaitlist(true)} data-testid="cta-waitlist-btn">Request access</button>
          </motion.div>
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer className="bunchh-footer" data-testid="footer">
        <div className="footer-inner">
          <div className="footer-grid">
            <div>
              <div className="footer-logo">
                <BunchhLogo height={28} />
              </div>
              <p className="footer-tagline">We help you do things you've been meaning to do, with people you might like.</p>
            </div>
            <div>
              <div className="footer-col-title">Company</div>
              <span className="footer-link" onClick={() => scrollToId('how')}>How it works</span>
              <span className="footer-link" onClick={() => scrollToId('faq')}>FAQ</span>
              <span className="footer-link" onClick={() => setShowWaitlist(true)}>Join waitlist</span>
            </div>
            <div>
              <div className="footer-col-title">Cities</div>
              <span className="footer-link">Mumbai</span>
              <span className="footer-link">Delhi</span>
              <span className="footer-link">Bangalore</span>
              <span className="footer-link">Pune</span>
            </div>
            <div>
              <div className="footer-col-title">Connect</div>
              <a className="footer-link" href="https://instagram.com/bunchh" target="_blank" rel="noopener noreferrer">Instagram</a>
              <a className="footer-link" href="mailto:community@bunchh.in">Email us</a>
            </div>
          </div>
          <div className="footer-bottom">
            <span>&copy; {new Date().getFullYear()} Bunchh. All rights reserved.</span>
            <span>Made in Mumbai.</span>
          </div>
        </div>
      </footer>

      {/* MODALS */}
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      {showWaitlist && <WaitlistModal onClose={() => setShowWaitlist(false)} />}
    </div>
  );
}
